import moment from 'moment';
import 'moment/locale/pl';
import 'moment/locale/en-gb';
import { ReactNode, useEffect, useMemo } from 'react';
import { Calendar, CalendarProps, DateLocalizer, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop, { withDragAndDropProps } from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './MagellanCalendar.scss';
import CalendarTopbar from './topbar/CalendarTopbar';
import CalendarShowMore from './show-more/CalendarShowMore';
import { useTranslation } from 'react-i18next';

moment.updateLocale('pl', { week: { dow: 1 } });

const now = momentLocalizer(moment);

const mapI18nToMomentLocale = (lng: string) => {
  const base = lng.toLowerCase().split('-')[0];
  if (base === 'pl') return 'pl';
  if (base === 'en') return 'en-gb'; // lub "en"
  return 'en-gb';
};

interface IMagellanCalendarProps<TEvent extends object, TResource extends object>
  extends
    Omit<CalendarProps<TEvent, TResource>, 'localizer'>,
    withDragAndDropProps<TEvent, TResource> {
  localizer?: DateLocalizer;
  moreHeader?: ReactNode;
  topbarHeader?: ReactNode;
}

const MagellanCalendar = <TEvent extends object, TResource extends object>({
  localizer = now,
  defaultDate = moment().toDate(),
  defaultView = 'month',
  moreHeader,
  topbarHeader,
  ...dndCalendarProps
}: IMagellanCalendarProps<TEvent, TResource>) => {
  const { i18n, t } = useTranslation();
  const culture = useMemo(() => mapI18nToMomentLocale(i18n.language), [i18n.language]);

  useEffect(() => {
    moment.locale(culture);
  }, [culture]);
  const DnDCalendar = withDragAndDrop<TEvent, TResource>(Calendar);

  return (
    <div className="magellan-calendar-wrapper">
      <DnDCalendar
        formats={{
          timeGutterFormat: (date) => ` ${date.getHours()} `,
        }}
        localizer={localizer}
        culture={culture}
        defaultDate={defaultDate}
        defaultView={defaultView}
        views={['month']}
        resizable={false}
        draggableAccessor={() => false} // Wyłącza drag dla wszystkich wydarzeń
        resizableAccessor={() => false} // Wyłącza zmianę rozmiaru
        selectable={false} // Wyłącza zaznaczanie zakresu
        {...dndCalendarProps}
        components={{
          toolbar: (props) => <CalendarTopbar {...props} topbarHeader={topbarHeader} />,

          showMore: (props) => (
            <CalendarShowMore
              {...props}
              eventComponent={dndCalendarProps.components?.event}
              moreHeader={moreHeader}
            />
          ),

          ...dndCalendarProps.components,
          month: {
            ...dndCalendarProps.components?.month,
          },
        }}
        className="magellan-calendar"
      />
    </div>
  );
};

export default MagellanCalendar;
